import React, { useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { BaseAgentChat } from './ai-agent';
import { useAgentPanel } from './AgentPanelContext';

function ContextChips() {
  const { interactionContext, clearInteractionContext, pageContext } = useAgentPanel();
  // Only name something specific (an app, a plan); page names are noise.
  const pageLabel = pageContext?.view_name || pageContext?.label || null;
  if (!interactionContext?.label && !pageLabel) return null;
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-3 pb-1 pt-2" data-testid="conversation-context">
      {pageLabel ? (
        <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600" title="The agent sees what you are looking at">
          <span className="text-slate-400">Looking at</span>
          <span className="truncate">{pageLabel}</span>
        </span>
      ) : null}
      {interactionContext?.label ? (
        <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-800">
          <span className="truncate">{interactionContext.label}</span>
          <button
            type="button"
            onClick={() => clearInteractionContext(interactionContext.source)}
            className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200"
            aria-label={`Remove ${interactionContext.label}`}
          >
            <X size={11} />
          </button>
        </span>
      ) : null}
    </div>
  );
}

/**
 * The open chat with the selected agent: the panel body.
 * Every chat is the same agent; "New chat" in the header starts another.
 */
export default function Conversation({ className = '' }) {
  const panel = useAgentPanel();
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    cancelStream,
    isStreaming,
    draftMessage,
    setDraftMessage,
    chatAgentDisplayName,
    chatAgentDescription,
    chatAgentStarterPrompts,
    startNewChat,
    composerFocusRequest,
    chatError,
    historyError,
  } = panel;

  useEffect(() => {
    if (!composerFocusRequest) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [composerFocusRequest]);

  const handleCancel = useCallback(() => cancelStream(), [cancelStream]);

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      {chatError || historyError ? (
        <p role="alert" className="shrink-0 border-b border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {chatError || historyError}
          <button type="button" onClick={() => startNewChat()} className="ml-2 font-semibold underline">Start a new chat</button>
        </p>
      ) : null}
      {panel.sessionMetadata?.session_kind === 'initiative' ? (
        <p className="shrink-0 border-b border-slate-100 px-3 py-2 text-xs text-slate-600">Initiative · {panel.sessionMetadata.initiative_title || 'Autonomous work'}</p>
      ) : null}
      {panel.connectionState === 'reconnecting' ? <p role="status" className="px-3 py-1 text-xs text-amber-700">Reconnecting to live updates. The agent can continue working.</p> : null}
      <ContextChips />
      <div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
        <BaseAgentChat
          composerContextKey={`${panel.agentId}:${panel.composerRevision}:${panel.sessionId}`}
          messages={messages}
          isLoading={isLoading}
          disabled={Boolean(panel.isLoadingHistory || historyError || panel.sessionMetadata?.can_send === false)}
          error={error}
          sendMessage={sendMessage}
          retryInput={panel.retryInput}
          cancelStream={handleCancel}
          isStreaming={isStreaming}
          canStop={panel.sessionMetadata?.can_stop !== false}
          message={draftMessage}
          setMessage={setDraftMessage}
          inputRef={inputRef}
          messagesEndRef={messagesEndRef}
          latestContextUsage={panel.latestContextUsage}
          isLoadingContextUsage={panel.isLoadingContextUsage}
          agentDisplayName={chatAgentDisplayName}
          agentDescription={chatAgentDescription}
          agentStarterPrompts={chatAgentStarterPrompts}
          agentUi={panel.agent ? panel.agent.ui || {} : null}
          onNewSession={startNewChat}
          showWork={Boolean(panel.showWork)}
        />
      </div>
    </div>
  );
}
